import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import NavBar from '../components/NavBar';

export default function Signup() {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { signUp, verifyEmail, resendVerificationEmail, isLoading, error, clearError } = useAuthStore();
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);

  // Handle navigation state from Login page (unverified user)
  useEffect(() => {
    const state = location.state as { email?: string; showVerification?: boolean; message?: string } | null;
    if (state?.email) {
      setEmail(state.email);
      if (state.showVerification) {
        setShowVerification(true);
      }
      if (state.message) {
        setInfoMessage(state.message);
      }
    }
  }, [location]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setInfoMessage(null);
    
    try {
      const result = await signUp(email, password, name);
      // Check if signup returned requiresVerification (user exists but unverified)
      if (result?.requiresVerification) {
        setShowVerification(true);
        setInfoMessage(result.message || 'A new verification code has been sent to your email.');
      } else {
        setShowVerification(true);
      }
    } catch (err: any) {
      // Check if error message indicates verification code was sent
      const errorMessage = err.message || '';
      if (errorMessage.includes('verification code has been sent') || 
          errorMessage.includes('An account with this email already exists')) {
        // User exists but may be unverified - show verification screen
        setShowVerification(true);
        setInfoMessage('A verification code has been sent to your email. Please check your inbox.');
      }
      // Error is also handled by the store (will be displayed if not handled above)
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    
    try {
      await verifyEmail(email, confirmationCode);
      navigate('/login');
    } catch (err) {
      // Error is handled by the store
    }
  };

  const handleResend = async () => {
    clearError();
    setInfoMessage(null);
    
    // Always show verification screen when resending code
    setShowVerification(true);
    
    try {
      await resendVerificationEmail(email);
      setInfoMessage('A new verification code has been sent to your email.');
    } catch (err) {
      // Error is handled by the store
    }
  };

  if (showVerification) {
    return (
      <div className={`min-h-screen ${tokens.bg.gradient} flex flex-col relative`}>
        <NavBar className={`${tokens.card.bg} shadow-sm`} containerClassName="px-3 sm:px-4">
          <h1 className={`text-xl font-bold ${tokens.text.primary}`}>Runow</h1>
        </NavBar>

        {/* Main Content */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollable">
          <div className="min-h-full flex items-start sm:items-center justify-center p-4">
            <div className={`max-w-md w-full ${tokens.card.bg} rounded-landing ${tokens.card.shadow} p-6 sm:p-8`}>
          <h2 className={`text-lg font-semibold ${tokens.text.primary} mb-6 text-center`}>
            Verify Your Email
          </h2>
          <p className={`text-xs ${tokens.text.muted} mb-4 text-center`}>
            We sent a verification code to <strong className={tokens.text.primary}>{email}</strong>
          </p>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-sm p-4">
              <p className="text-red-800 text-xs">{error}</p>
            </div>
          )}

          {infoMessage && (
            <p className={`text-xs ${tokens.text.muted} mb-4 text-center`}>
              {infoMessage}
            </p>
          )}

          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label htmlFor="code" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
                Verification Code
              </label>
              <input
                id="code"
                type="text"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                required
                className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
                placeholder="Enter 6-digit code"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full ${tokens.button.primary} disabled:opacity-50 disabled:cursor-not-allowed font-semibold py-3 px-4 transition-colors min-h-[48px]`}
            >
              {isLoading ? 'Verifying...' : 'Verify Email'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={handleResend}
              disabled={isLoading}
              className={`${tokens.text.accent} hover:opacity-80 font-medium text-sm transition-opacity disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]`}
            >
              {isLoading ? 'Sending...' : "Didn't receive the code? Resend"}
            </button>
          </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${tokens.bg.gradient} flex flex-col relative`}>
      <NavBar className={`${tokens.card.bg} shadow-sm`} containerClassName="px-3 sm:px-4">
        <h1 className={`text-xl font-bold ${tokens.text.primary}`}>Runow</h1>
      </NavBar>

      {/* Main Content */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollable">
        <div className="min-h-full flex items-start sm:items-center justify-center p-4">
          <div className={`max-w-md w-full ${tokens.card.bg} rounded-landing ${tokens.card.shadow} p-6 sm:p-8`}>
        <h1 className={`text-2xl font-bold ${tokens.text.primary} mb-6 text-center`}>
          Runow
        </h1>
        <h2 className={`text-lg font-semibold ${tokens.text.primary} mb-6 text-center`}>
          Create Account
        </h2>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-sm p-4">
            <p className="text-red-800 text-xs">{error}</p>
          </div>
        )}

        <form onSubmit={handleSignUp} className="space-y-4">
          <div>
            <label htmlFor="name" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
              Name (Optional)
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="Your name"
            />
          </div>

          <div>
            <label htmlFor="email" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className={`w-full px-4 py-3 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
              placeholder="At least 8 characters"
            />
            <p className={`mt-1 text-xs ${tokens.text.muted}`}>
              Must be at least 8 characters with uppercase, lowercase, and numbers
            </p>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full ${tokens.button.primary} disabled:opacity-50 disabled:cursor-not-allowed font-semibold py-3 px-4 transition-colors min-h-[48px]`}
          >
            {isLoading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className={`text-sm ${tokens.text.muted}`}>
            Already have an account?{' '}
            <Link to="/login" className={`${tokens.text.accent} hover:opacity-80 font-medium transition-opacity`}>
              Sign in
            </Link>
          </p>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
}

